// Centralized website template definitions + theme tokens.
// Keeps Storefront (admin) and LandingPage (public) in sync.

export const STORE_TEMPLATES = [
  {
    key: 'classic',
    labelKey: 'homepage_1_classic',
    fallbackLabel: 'Classic',
    description: 'Balanced hero, map-first, and trust sections.',
    tags: ['Hero', 'Map', 'How it works'],
    status: 'ready',
  },
  {
    key: 'metro',
    labelKey: 'homepage_2_metro',
    fallbackLabel: 'Metro',
    description: 'Bold gradient, card rails, fast scanning.',
    tags: ['Hero', 'Value props', 'Download'],
    status: 'ready',
  },
  {
    key: 'minima',
    labelKey: 'homepage_3_minima',
    fallbackLabel: 'Minimal',
    description: 'Crisp whitespace, editorial rhythm, premium feel.',
    tags: ['Hero', 'Highlights', 'Footer'],
    status: 'ready',
  },
  {
    key: 'megamart',
    labelKey: 'homepage_4_megamart',
    fallbackLabel: 'Commerce',
    description: 'Shop-forward layout for future storefront pages.',
    tags: ['Hero', 'Shop', 'CTA'],
    status: 'beta',
  },
  {
    key: 'reclassic',
    labelKey: 'homepage_5_reclassic',
    fallbackLabel: 'Re-Classic',
    description: 'Warm brand story with strong copy blocks.',
    tags: ['Hero', 'Story', 'Safety'],
    status: 'beta',
  },
  {
    key: 'thecore',
    labelKey: 'homepage_6_thecore',
    fallbackLabel: 'Core',
    description: 'Dark tech aesthetic with sharp contrasts.',
    tags: ['Hero', 'KPIs', 'Map'],
    status: 'beta',
  },
];

// Some deployments may still have older keys stored.
const LEGACY_TEMPLATE_KEYS = ['aura', 'minimal', 'sunrise'];

export const ALLOWED_TEMPLATE_KEYS = new Set([
  ...STORE_TEMPLATES.map((t) => t.key),
  ...LEGACY_TEMPLATE_KEYS,
]);

export const normalizeTemplateKey = (key) => {
  if (typeof key !== 'string') return 'classic';
  return ALLOWED_TEMPLATE_KEYS.has(key) ? key : 'classic';
};

export const HERO_THEMES = {
  classic: {
    variant: 'classic',
    background: '#0b0b0b',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.7)',
    primaryButtonBg: '#ffffff',
    primaryButtonText: '#0b0b0b',
    primaryButtonHover: '#f8fafc',
    secondaryButtonText: '#ffffff',
    secondaryButtonBorder: 'rgba(255,255,255,0.35)',
    secondaryButtonHoverBg: 'rgba(255,255,255,0.12)',
    cardBg: '#ffffff',
    cardText: '#0b0b0b',
  },
  aura: {
    variant: 'classic',
    background: '#0b0b0b',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.7)',
    primaryButtonBg: '#ffffff',
    primaryButtonText: '#0b0b0b',
    primaryButtonHover: '#f8fafc',
    secondaryButtonText: '#ffffff',
    secondaryButtonBorder: 'rgba(255,255,255,0.35)',
    secondaryButtonHoverBg: 'rgba(255,255,255,0.12)',
    cardBg: '#ffffff',
    cardText: '#0b0b0b',
  },
  metro: {
    variant: 'metro',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 60%)',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.7)',
    primaryButtonBg: '#38bdf8',
    primaryButtonText: '#0b0b0b',
    primaryButtonHover: '#7dd3fc',
    secondaryButtonText: '#ffffff',
    secondaryButtonBorder: 'rgba(255,255,255,0.45)',
    secondaryButtonHoverBg: 'rgba(15,23,42,0.3)',
    cardBg: '#ffffff',
    cardText: '#0b0b0b',
  },
  minima: {
    variant: 'minimal',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 70%)',
    text: '#0f172a',
    subtext: 'rgba(15,23,42,0.7)',
    primaryButtonBg: '#0f172a',
    primaryButtonText: '#ffffff',
    primaryButtonHover: '#1e293b',
    secondaryButtonText: '#0f172a',
    secondaryButtonBorder: 'rgba(15,23,42,0.25)',
    secondaryButtonHoverBg: 'rgba(148,163,184,0.3)',
    cardBg: '#ffffff',
    cardText: '#0f172a',
  },
  minimal: {
    variant: 'minimal',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 70%)',
    text: '#0f172a',
    subtext: 'rgba(15,23,42,0.7)',
    primaryButtonBg: '#0f172a',
    primaryButtonText: '#ffffff',
    primaryButtonHover: '#1e293b',
    secondaryButtonText: '#0f172a',
    secondaryButtonBorder: 'rgba(15,23,42,0.25)',
    secondaryButtonHoverBg: 'rgba(148,163,184,0.3)',
    cardBg: '#ffffff',
    cardText: '#0f172a',
  },
  sunrise: {
    variant: 'sunrise',
    background: 'linear-gradient(135deg, #f97316 0%, #facc15 60%)',
    text: '#0f172a',
    subtext: 'rgba(15,23,42,0.75)',
    primaryButtonBg: '#0f172a',
    primaryButtonText: '#ffffff',
    primaryButtonHover: '#1f2937',
    secondaryButtonText: '#0f172a',
    secondaryButtonBorder: 'rgba(15,23,42,0.4)',
    secondaryButtonHoverBg: 'rgba(255,255,255,0.2)',
    cardBg: '#ffffff',
    cardText: '#0f172a',
  },
  megamart: {
    variant: 'commerce',
    background: 'linear-gradient(135deg, #0b1224 0%, #0f172a 55%, #064e3b 100%)',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.75)',
    primaryButtonBg: '#22c55e',
    primaryButtonText: '#052e16',
    primaryButtonHover: '#34d399',
    secondaryButtonText: '#ffffff',
    secondaryButtonBorder: 'rgba(255,255,255,0.45)',
    secondaryButtonHoverBg: 'rgba(255,255,255,0.12)',
    cardBg: '#ffffff',
    cardText: '#0b0b0b',
  },
  reclassic: {
    variant: 'classic',
    background: 'linear-gradient(135deg, #0b0b0b 0%, #111827 55%, #0b0b0b 100%)',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.7)',
    primaryButtonBg: '#ffffff',
    primaryButtonText: '#0b0b0b',
    primaryButtonHover: '#f8fafc',
    secondaryButtonText: '#ffffff',
    secondaryButtonBorder: 'rgba(255,255,255,0.35)',
    secondaryButtonHoverBg: 'rgba(255,255,255,0.12)',
    cardBg: '#ffffff',
    cardText: '#0b0b0b',
  },
  thecore: {
    variant: 'core',
    background: 'linear-gradient(135deg, #0b1120 0%, #111827 60%)',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.7)',
    primaryButtonBg: '#38bdf8',
    primaryButtonText: '#0b0b0b',
    primaryButtonHover: '#7dd3fc',
    secondaryButtonText: '#ffffff',
    secondaryButtonBorder: 'rgba(255,255,255,0.45)',
    secondaryButtonHoverBg: 'rgba(15,23,42,0.3)',
    cardBg: '#ffffff',
    cardText: '#0b0b0b',
  },
};

export const SITE_THEMES = {
  classic: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#f8fafc',
    cardAltBg: '#f1f5f9',
    cardBg: '#ffffff',
    border: 'rgba(15,23,42,0.10)',
    text: '#0b0b0b',
    muted: '#475569',
    link: '#0b0b0b',
    darkSectionBg: '#0b0b0b',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.7)',
    accent: '#16a34a',
    headerColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
  aura: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#f8fafc',
    cardAltBg: '#f1f5f9',
    cardBg: '#ffffff',
    border: 'rgba(15,23,42,0.10)',
    text: '#0b0b0b',
    muted: '#475569',
    link: '#0b0b0b',
    darkSectionBg: '#0b0b0b',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.7)',
    accent: '#f59e0b',
    headerColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
  metro: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#f0f9ff',
    cardAltBg: '#e0f2fe',
    cardBg: '#ffffff',
    border: 'rgba(2,132,199,0.18)',
    text: '#0f172a',
    muted: '#475569',
    link: '#0f172a',
    darkSectionBg: '#0f172a',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#38bdf8',
    headerColor: 'transparent',
    headerScrollColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
  minima: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#f8fafc',
    cardAltBg: '#ffffff',
    cardBg: '#ffffff',
    border: '#e2e8f0',
    text: '#0f172a',
    muted: '#64748b',
    link: '#0f172a',
    darkSectionBg: '#0f172a',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#0f172a',
    headerColor: 'white',
    navMode: 'light',
    footer: { mode: 'light' },
  },
  minimal: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#f8fafc',
    cardAltBg: '#ffffff',
    cardBg: '#ffffff',
    border: '#e2e8f0',
    text: '#0f172a',
    muted: '#64748b',
    link: '#0f172a',
    darkSectionBg: '#0f172a',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#0f172a',
    headerColor: 'white',
    navMode: 'light',
    footer: { mode: 'light' },
  },
  sunrise: {
    pageBg: '#fff7ed',
    sectionBg: '#fff7ed',
    sectionAltBg: '#fffbeb',
    cardAltBg: '#ffffff',
    cardBg: '#ffffff',
    border: 'rgba(249,115,22,0.18)',
    text: '#0f172a',
    muted: 'rgba(15,23,42,0.7)',
    link: '#0f172a',
    darkSectionBg: '#0f172a',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#f97316',
    headerColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
  megamart: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#f0fdf4',
    cardAltBg: '#dcfce7',
    cardBg: '#ffffff',
    border: 'rgba(22,163,74,0.18)',
    text: '#052e16',
    muted: 'rgba(5,46,22,0.72)',
    link: '#052e16',
    darkSectionBg: '#052e16',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#16a34a',
    headerColor: 'transparent',
    headerScrollColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
  reclassic: {
    pageBg: '#ffffff',
    sectionBg: '#ffffff',
    sectionAltBg: '#faf5ff',
    cardAltBg: '#f3e8ff',
    cardBg: '#ffffff',
    border: 'rgba(147,51,234,0.14)',
    text: '#0f172a',
    muted: '#475569',
    link: '#0f172a',
    darkSectionBg: '#111827',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#7c3aed',
    headerColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
  thecore: {
    pageBg: '#0b1120',
    sectionBg: '#0b1120',
    sectionAltBg: '#0f172a',
    cardAltBg: 'rgba(255,255,255,0.06)',
    cardBg: '#ffffff',
    border: 'rgba(255,255,255,0.10)',
    text: '#ffffff',
    muted: 'rgba(255,255,255,0.72)',
    link: '#ffffff',
    darkSectionBg: '#0b1120',
    darkSectionText: '#ffffff',
    darkSectionMuted: 'rgba(255,255,255,0.72)',
    accent: '#38bdf8',
    headerColor: 'transparent',
    headerScrollColor: 'dark',
    navMode: 'dark',
    footer: { mode: 'dark' },
  },
};

export const getHeroTheme = (templateKey) => {
  const normalized = normalizeTemplateKey(templateKey);
  return HERO_THEMES[normalized] || HERO_THEMES.classic;
};

export const getSiteTheme = (templateKey, appearance = {}) => {
  const normalized = normalizeTemplateKey(templateKey);
  const baseTheme = SITE_THEMES[normalized] || SITE_THEMES.classic;
  return {
    ...baseTheme,
    ...appearance,
    footer: { ...(baseTheme.footer || {}), ...(appearance.footer || {}) },
  };
};

export const DEFAULT_SECTION_ORDERS = {
  classic: ['hero', 'map', 'howItWorks', 'valueProps', 'driverBusiness', 'download'],
  metro: ['hero', 'howItWorks', 'valueProps', 'map', 'driverBusiness', 'download'],
  minima: ['hero', 'valueProps', 'howItWorks', 'download', 'map'],
  megamart: ['hero', 'valueProps', 'download', 'map', 'howItWorks', 'driverBusiness'],
  reclassic: ['hero', 'howItWorks', 'valueProps', 'map', 'download'],
  thecore: ['hero', 'map', 'valueProps', 'howItWorks', 'driverBusiness', 'download'],
};

export const getDefaultSectionOrder = (templateKey) => {
  const normalized = normalizeTemplateKey(templateKey);
  return DEFAULT_SECTION_ORDERS[normalized] || DEFAULT_SECTION_ORDERS.classic;
};

